'use client'

/**
 * Stage 37 — «Другие возможные связи» (collapsed).
 *
 * Replaces the old competition-cluster section and the button that unfolded it. Same
 * data, same server-side paging, but described the way the case itself is described:
 * shipment numbers, transport unit, cargo, route, the two scores and one short phrase on
 * how the candidate differs from the link currently open.
 *
 * Cluster id, representative status, ranks, candidate counts and membership flags are
 * NOT here — they are catalog diagnostics and live in «Технические сведения».
 *
 * Paging stays mandatory: the largest cluster holds tens of thousands of links, so this
 * component renders exactly one server page and never receives a full list.
 */
import { useT, useFormat } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { buildRelatedLink } from '../cross-unit.case-view'
import type { CandidatePagination, CandidatePairItem } from '../cross-unit.types'

export function RelatedLinks({
  open,
  onToggle,
  items,
  pagination,
  onPage,
  onOpenPair,
  currentPairId,
  current,
  isLoading,
}: {
  open: boolean
  onToggle: () => void
  items: CandidatePairItem[]
  pagination?: CandidatePagination
  onPage: (p: number) => void
  onOpenPair?: (pairId: string) => void
  currentPairId?: string
  /** The open link's own facts — used only to phrase «чем отличается». */
  current?: { importShipmentId?: string | null; exportShipmentId?: string | null; daysBetween?: number | null }
  isLoading?: boolean
}) {
  const t = useT()
  const f = useFormat()
  const page = pagination?.page ?? 1
  const totalPages = Math.max(1, pagination?.totalPages ?? 1)
  const total = pagination?.totalItems ?? items.length

  return (
    <section data-block="related-links" className="rounded-[4px] border border-[var(--border-subtle)]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
      >
        {open ? '▾' : '▸'} {t('analytics.links.title')}
        {open && pagination ? ` (${f.int(total)})` : ''}
      </button>
      {open && (
        <div className="space-y-2 border-t border-[var(--border-subtle)] p-3">
          {isLoading ? (
            <p className="text-[12px] text-[var(--text-tertiary)]">{t('common.state.loading')}</p>
          ) : items.length === 0 ? (
            <p className="text-[12px] text-[var(--text-tertiary)]">
              {t('analytics.links.empty')}
            </p>
          ) : (
            <>
              <div className="space-y-2">
                {items.map((it) => {
                  const r = buildRelatedLink(t, it, current)
                  const isCurrent = r.pairId === currentPairId
                  return (
                    <div
                      key={r.pairId}
                      data-related-id={r.pairId}
                      data-current={isCurrent ? 'true' : 'false'}
                      onClick={onOpenPair && !isCurrent ? () => onOpenPair(r.pairId) : undefined}
                      className={cn(
                        'rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2.5',
                        isCurrent ? 'bg-[var(--bg-subtle)]' : onOpenPair && 'cursor-pointer hover:bg-[var(--bg-subtle)]',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12px] text-[var(--text-primary)]">
                          {r.shipments
                            ? t('analytics.links.shipments', { value: r.shipments })
                            : t('analytics.links.pair', { value: r.pairId.slice(0, 8) })}
                        </span>
                        <span className="flex items-center gap-2 text-[11px]">
                          {r.anomalyText && (
                            <span className="font-mono tabular-nums text-[var(--text-secondary)]">
                              {t('analytics.links.anomaly', { value: r.anomalyText })}
                            </span>
                          )}
                          {r.mlText && (
                            <span className="font-mono tabular-nums text-[var(--text-secondary)]">
                              {t('analytics.links.priority', { value: r.mlText })}
                            </span>
                          )}
                        </span>
                      </div>
                      {(r.unit || r.cargo || r.route) && (
                        <div className="mt-1 text-[11px] text-[var(--text-secondary)]">
                          {[r.unit, r.cargo, r.route].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {r.difference && (
                        <div className="mt-0.5 text-[10px] text-[var(--text-tertiary)]">
                          {t('analytics.links.difference', { value: r.difference })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center justify-between text-[11px] text-[var(--text-secondary)]">
                <span>{t('analytics.links.total', { value: f.int(total) })}</span>
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onPage(Math.max(1, page - 1))}
                    className="rounded-[3px] border border-[var(--border-default)] px-2 py-0.5 disabled:opacity-40"
                  >
                    ←
                  </button>
                  {t('analytics.links.page', { page, total: totalPages })}
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => onPage(Math.min(totalPages, page + 1))}
                    className="rounded-[3px] border border-[var(--border-default)] px-2 py-0.5 disabled:opacity-40"
                  >
                    →
                  </button>
                </span>
              </div>
            </>
          )}
          <p className="text-[10px] leading-relaxed text-[var(--text-tertiary)]">
            {t('analytics.links.note')}
          </p>
        </div>
      )}
    </section>
  )
}
